import { Button, Card, Chip, Divider, Icons, Stack, styled, Tooltip, Typography } from '@healthinal/ui';
import { useState, type MouseEvent } from 'react';
import {
  mapTherapyDirectlyObservedTherapyToEntries,
  useGetDirectlyObservedTherapyByTherapySuspense,
  type DirectlyObservedTherapyEntryDto,
} from '../../../../../api/created/directlyObservedTherapy.ts';
import { TherapyDto } from '../../../../../api/generated/model';
import { formatDateWithGermanMonth } from '../../../../../utils/date.ts';
import { toDotFrequencyDisplayLabel } from '../../../../../utils/dotFrequency.ts';
import { PatientAddressDisplay } from '../../../../components/patient-address/PatientAddressDisplay.tsx';
import { CreateDirectlyObservedTherapyDialog } from './CreateDirectlyObservedTherapyDialog.tsx';
import { EndDirectlyObservedTherapyDialog } from './EndDirectlyObservedTherapyDialog.tsx';

const DOT_CARD_HEIGHT = 560;

export function DirectlyObservedTherapyCard({ patientId, therapy }: { patientId: string; therapy?: TherapyDto }) {
  const therapyId = therapy?.id ?? '';
  const { data: directlyObservedTherapyEntries } = useGetDirectlyObservedTherapyByTherapySuspense(therapyId);
  const entries =
    directlyObservedTherapyEntries.length > 0
      ? directlyObservedTherapyEntries
      : mapTherapyDirectlyObservedTherapyToEntries(therapy?.directlyObservedTherapy);
  const [isCreateDirectlyObservedTherapyDialogOpen, setIsCreateDirectlyObservedTherapyDialogOpen] = useState(false);
  const [endDirectlyObservedTherapyEntry, setEndDirectlyObservedTherapyEntry] =
    useState<DirectlyObservedTherapyEntryDto | null>(null);
  const [expandedEntryIds, setExpandedEntryIds] = useState<Set<string>>(() => {
    const activeEntry = entries.find((entry) => entry.status === 'ACTIVE');
    return activeEntry ? new Set([activeEntry.id]) : new Set();
  });
  const doesActiveTherapyExist = therapy && !therapy.finished;

  const toggleEntryExpanded = (entryId: string) => {
    setExpandedEntryIds((current) => {
      const next = new Set(current);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  return (
    <>
      <Card
        sx={{
          height: DOT_CARD_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ flexShrink: 0 }}>
          <Typography level="title-lg">DOT</Typography>
          {doesActiveTherapyExist && (
            <Tooltip
              disableHoverListener={!!doesActiveTherapyExist}
              title={'Um eine DOT erfassen zu können muss eine aktive Therapie vorhanden sein.'}>
              <StyledButton
                variant={doesActiveTherapyExist ? 'solid' : 'soft'}
                color={'neutral'}
                sx={!doesActiveTherapyExist ? { cursor: 'default' } : { cursor: 'pointer' }}
                onClick={() => doesActiveTherapyExist && setIsCreateDirectlyObservedTherapyDialogOpen(true)}>
                DOT erfassen
              </StyledButton>
            </Tooltip>
          )}
        </Stack>
        {entries.length > 0 && <Divider sx={{ my: 2, flexShrink: 0 }} />}
        <Stack
          gap={1}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: entries.length > 0 ? 'auto' : 'hidden',
            overflowX: 'hidden',
            pr: 0.5,
            alignItems: entries.length === 0 ? 'center' : undefined,
            justifyContent: entries.length === 0 ? 'center' : undefined,
          }}>
          {entries.length === 0 ? (
            <Stack alignItems={'center'} gap={1}>
              <Icons.Description />
              <Chip>Nicht erfasst</Chip>
            </Stack>
          ) : (
            entries.map((entry) => (
              <DirectlyObservedTherapyEntryRow
                key={entry.id}
                entry={entry}
                isExpanded={expandedEntryIds.has(entry.id)}
                onToggleExpanded={() => toggleEntryExpanded(entry.id)}
                onEdit={() => setEndDirectlyObservedTherapyEntry(entry)}
              />
            ))
          )}
        </Stack>
      </Card>
      <CreateDirectlyObservedTherapyDialog
        open={isCreateDirectlyObservedTherapyDialogOpen}
        onClose={() => setIsCreateDirectlyObservedTherapyDialogOpen(false)}
        patientId={patientId}
        therapyId={therapyId}
      />
      {endDirectlyObservedTherapyEntry && (
        <EndDirectlyObservedTherapyDialog
          open={!!endDirectlyObservedTherapyEntry}
          onClose={() => setEndDirectlyObservedTherapyEntry(null)}
          entry={endDirectlyObservedTherapyEntry}
          patientId={patientId}
          therapyId={therapyId}
        />
      )}
    </>
  );
}

function DirectlyObservedTherapyEntryRow({
  entry,
  isExpanded,
  onToggleExpanded,
  onEdit,
}: {
  entry: DirectlyObservedTherapyEntryDto;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onEdit: () => void;
}) {
  const frequenzLabel = toDotFrequencyDisplayLabel(entry.frequenz) || '-';
  const dateRange = `${formatDateWithGermanMonth(entry.from)} - ${entry.until ? formatDateWithGermanMonth(entry.until) : 'Jetzt'}`;

  return (
    <Stack
      gap={1}
      sx={(theme) => ({
        border: '1px solid',
        borderColor: theme.palette.neutral.outlinedBorder,
        borderRadius: theme.radius.sm,
        p: 1.25,
      })}>
      <Stack
        direction={'row'}
        alignItems={'center'}
        gap={1}
        onClick={onToggleExpanded}
        sx={{ cursor: 'pointer', userSelect: 'none' }}>
        <Typography level={'title-md'}>DOT</Typography>
        <DirectlyObservedTherapyStatusChip status={entry.status} onEdit={onEdit} />
        <Typography flex={1} level={'title-sm'} textAlign={'right'}>
          {dateRange}
        </Typography>
        {isExpanded ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
      </Stack>
      {isExpanded && (
        <Stack gap={2} sx={{ pl: 1 }}>
          <Stack direction={'row'}>
            <Typography flex={1} level={'title-md'}>
              Frequenz
            </Typography>
            <Typography flex={3} level={'title-sm'}>
              {frequenzLabel}
            </Typography>
          </Stack>
          {entry.applicant && (
            <Stack direction={'row'}>
              <Typography flex={1} level={'title-md'}>
                Auftraggeber
              </Typography>
              <PatientAddressDisplay flex={3} patientAddress={entry.applicant} />
            </Stack>
          )}
          {entry.executor && (
            <Stack direction={'row'}>
              <Typography flex={1} level={'title-md'}>
                Durchführer
              </Typography>
              <PatientAddressDisplay flex={3} patientAddress={entry.executor} />
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function DirectlyObservedTherapyStatusChip({
  status,
  onEdit,
}: {
  status: DirectlyObservedTherapyEntryDto['status'];
  onEdit: () => void;
}) {
  const openEditDialog = (event: MouseEvent) => {
    event.stopPropagation();
    onEdit();
  };

  switch (status) {
    case 'ACTIVE':
      return (
        <Chip color={'success'} variant={'solid'} endDecorator={<Icons.Edit />} onClick={openEditDialog}>
          Aktiv
        </Chip>
      );
    case 'PAST':
      return (
        <Chip color={'neutral'} variant={'solid'} endDecorator={<Icons.Edit />} onClick={openEditDialog}>
          Vergangen
        </Chip>
      );
    case 'FUTURE':
      return (
        <Chip color={'neutral'} variant={'solid'} endDecorator={<Icons.Edit />} onClick={openEditDialog}>
          Zukünftig
        </Chip>
      );
  }
}

const StyledButton = styled(Button)(({ theme }) => ({
  '--variant-softHoverBg': theme.palette.neutral.softBg,
  '--variant-softActiveBg': theme.palette.neutral.softBg,
  '--variant-softActiveColor': theme.palette.neutral.softColor,
}));
